import { ReactNode, useState } from "react";
import {
  useFloating,
  shift,
  offset,
  flip,
  autoUpdate,
  useDismiss,
  useRole,
  useInteractions,
  useClick,
  useTransitionStyles,
} from "@floating-ui/react";
import { tv, VariantProps } from "tailwind-variants";
import DropdownItem, {
  IDropdownItem,
} from "@/components/Dropdown/DropdownItem";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";

const dropdownStyles = tv({
  slots: {
    base: "relative inline-flex",
    buttonWrapper: "cursor-pointer",
    button:
      "flex h-50 w-50 cursor-pointer items-center justify-center rounded-full bg-white text-primary transition-colors hover:bg-gray-light",
    dropdown:
      "max-h-[285px] min-w-[300px] overflow-scroll border-t-4 border-primary bg-white shadow-md",
    icon: "h-25 w-25",
  },
  variants: {
    buttonIsActive: {
      true: { button: "bg-secondary text-white hover:bg-secondary-light" },
    },
    variant: {
      outline: {
        base: "rounded-full border-2 border-secondary bg-transparent text-secondary hover:border-secondary-light hover:text-secondary-light",
        button: "h-[46px] w-[46px] bg-transparent text-secondary",
        dropdown: "border-none",
      },
    },
  },
  compoundSlots: [
    {
      slots: ["button"],
      variant: "outline",
      buttonIsActive: true,
      className: "bg-gray-light hover:bg-gray-light text-secondary-light",
    },
  ],
});

interface IProps {
  button?: {
    icon?: IconProp;
    iconProps?: Omit<FontAwesomeIconProps, "icon">;
    isActive?: boolean;
    override?: ReactNode;
  };
  items: IDropdownItem[];
  testId?: string;
  trailingStaticSlot?: ReactNode;
  variant?: VariantProps<typeof dropdownStyles>["variant"];
}

const Dropdown = ({
  button: { icon, isActive, override, iconProps } = {},
  items,
  testId = "dropdown",
  trailingStaticSlot,
  variant,
}: IProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const floatingOffset = variant === "outline" ? 10 : 30;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(floatingOffset),
      flip(),
      shift({
        padding: 38,
      }),
    ],
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
  });

  const { isMounted, styles } = useTransitionStyles(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });
  const click = useClick(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
    click,
  ]);

  const checkButtonIsActive = () => isActive || isOpen;

  const {
    base,
    buttonWrapper,
    button,
    dropdown,
    icon: iconStyles,
  } = dropdownStyles({
    buttonIsActive: checkButtonIsActive(),
    variant,
  });

  const renderDefaultButton = () => (
    <button className={button()}>
      <FontAwesomeIcon
        icon={icon || faQuestionCircle}
        fixedWidth
        className={iconStyles()}
        {...iconProps}
      />
    </button>
  );

  const renderDropdownItems = () =>
    items.map((item, index) => (
      <DropdownItem key={index} tabIndex={index} {...item} />
    ));

  return (
    <div
      ref={refs.setReference}
      className={base()}
      data-testid={testId}
      {...getReferenceProps()}>
      <div className={buttonWrapper()} data-testid={`${testId}-button`}>
        {!!override ? override : renderDefaultButton()}
      </div>

      {isMounted && (
        <div
          ref={refs.setFloating}
          style={{ zIndex: 100, ...floatingStyles }}
          data-testid={`${testId}-list`}
          {...getFloatingProps()}>
          <div style={styles} className={dropdown()}>
            {renderDropdownItems()}
          </div>
          <div style={styles}>{trailingStaticSlot}</div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
